/**
 * Store for managing the Help & Feedback dialog state.
 * Allows any part of the app to trigger the dialog opening with an
 * optional prefilled message (e.g. from a "Send feedback" button next
 * to a beta feature).
 */

import { create } from 'zustand';

type HelpFeedbackState = {
  isOpen: boolean;
  prefillMessage: string;
  /**
   * Read-only context prefix shown above the message textarea (e.g.
   * "Feedback on the Database Blueprint feature:"). Submitted with the user's
   * message so incoming feedback is auto-tagged. Empty string = no banner.
   */
  prefillPrefix: string;
  /** Increments on every programmatic open so the dialog can re-apply the prefill. */
  prefillToken: number;
};

type HelpFeedbackActions = {
  /**
   * Opens the feedback dialog. `prefillMessage` seeds the editable textarea;
   * `options.prefix` adds an immutable context banner above the textarea and
   * is prepended to the message on submit.
   */
  openDialog: (
    prefillMessage?: string,
    options?: { prefix?: string },
  ) => void;
  setOpen: (open: boolean) => void;
};

export const useHelpFeedbackStore = create<HelpFeedbackState & HelpFeedbackActions>((set) => ({
  isOpen: false,
  prefillMessage: '',
  prefillPrefix: '',
  prefillToken: 0,

  openDialog: (prefillMessage = '', options) =>
    set((state) => ({
      isOpen: true,
      prefillMessage,
      prefillPrefix: options?.prefix ?? '',
      prefillToken: state.prefillToken + 1,
    })),

  setOpen: (open) => set({ isOpen: open }),
}));
